import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase, checkUserAdminStatus } from '../lib/supabase';
import Logo from './Logo';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [mfaStep, setMfaStep] = useState<'password' | 'mfa'>('password');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  const { } = useAuth(); // eslint-disable-line no-empty-pattern
  const navigate = useNavigate();

  // Check if email has valid DealOnce domain
  const isValidDealOnceEmail = (email: string): boolean => {
    const emailLower = email.toLowerCase().trim();
    return emailLower.endsWith('@dealonce.com') || emailLower.endsWith('@dealonce.com.au');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions while processing
    if (loading) {
      return;
    }

    if (mfaStep === 'password') {
      if (!email || !password) {
        setError('Please fill in all fields');
        return;
      }

      // Check if email domain is valid for admin access
      if (!isValidDealOnceEmail(email)) {
        setError('This portal is only accessible to admins. Please use a valid admin email address.');
        return;
      }

      try {
        setLoading(true);
        setError('');
        setInfo('');
        
        // First, verify credentials without fully authenticating
        // We'll use a different approach to check credentials and MFA status
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });



        if (error) {
          throw error;
        }

        // Login successful, now check if user is admin and has MFA
        if (data.user) {
          // Check admin status first
          const { isAdmin: userIsAdmin } = await checkUserAdminStatus(data.user.id);
          
          if (!userIsAdmin) {
            // Sign out the user since they're not admin
            await supabase.auth.signOut();
            throw new Error('Access denied. Admin privileges required.');
          }
          
          // Check if user has MFA enabled
          const { data: mfaData, error: mfaError } = await supabase.auth.mfa.listFactors();
          
          if (!mfaError && mfaData && mfaData.totp && mfaData.totp.length > 0) {
            // User has MFA enabled, require verification
            const factor = mfaData.totp[0]; // Use first TOTP factor
            setMfaFactorId(factor.id);
            setMfaStep('mfa');
            setLoading(false); // Stop loading before switching to MFA step
            setError(''); // Clear any errors
            return; // Don't proceed to dashboard yet
          } else {
            // No MFA required, proceed to dashboard
            navigate('/dashboard');
          }
        }
        
      } catch (error: any) {
        let errorMessage = 'An error occurred during login. Please try again.';
        
        if (error.message) {
          if (error.message.includes('Invalid login credentials')) {
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          } else if (error.message.includes('Email not confirmed')) {
            errorMessage = 'Please confirm your email address before logging in.';
          } else if (error.message.includes('Too many requests')) {
            errorMessage = 'Too many login attempts. Please wait a moment before trying again.';
          } else {
            errorMessage = error.message;
          }
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    } else {
      // MFA step
      if (!mfaCode || mfaCode.length !== 6) {
        setError('Please enter a valid 6-digit code');
        return;
      }

      try {
        setLoading(true);
        setError('');

        if (!mfaFactorId) {
          throw new Error('No active MFA factor');
        }

        // Use Supabase's built-in MFA challenge verification
        const { error } = await supabase.auth.mfa.challengeAndVerify({
          factorId: mfaFactorId,
          code: mfaCode
        });

        if (error) {
          throw new Error(error.message || 'Invalid MFA code');
        }

        // MFA verification successful, now complete the login
        // User is already authenticated after MFA verification, just navigate to dashboard
        // The AuthContext will handle the admin status check via the auth state change listener
        navigate('/dashboard');
        
      } catch (error: any) {
        setError(error.message || 'Invalid MFA code. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Send password reset email to the entered address
  const handleSendPasswordReset = async () => {
    // Only available on password step
    if (mfaStep !== 'password') return;
    try {
      setLoading(true);
      setError('');
      setInfo('');

      if (!email) {
        setError('Please enter your email first');
        return;
      }

      // Check if email domain is valid for admin access
      if (!isValidDealOnceEmail(email)) {
        setError('This portal is only accessible to admins. Please use a valid admin email address.');
        return;
      }

      const redirectTo = (
        (process.env.REACT_APP_RESET_REDIRECT_URL && process.env.REACT_APP_RESET_REDIRECT_URL.trim())
        || (typeof window !== 'undefined' && window.location.hostname.endsWith('dealonce.com')
          ? 'https://admin.dealonce.com/reset-password'
          : `${window.location.origin}/reset-password`)
      );
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw error;

      setInfo('Password reset email sent. Check your inbox.');
    } catch (err: any) {
      setError(err?.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Enhanced Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo size={64} />
          </div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-3">
            {mfaStep === 'password' ? 'Admin Login' : 'MFA Verification'}
          </h2>
          <p className="text-lg text-gray-600">
            {mfaStep === 'password' 
              ? 'Sign in to access the admin dashboard'
              : 'Enter the 6-digit code from your authenticator app'
            }
          </p>
        </div>
        
        {/* Enhanced Form */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-soft border border-white/20 p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {mfaStep === 'password' ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 shadow-sm hover:shadow-md"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 shadow-sm hover:shadow-md"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <div className="mx-auto h-16 w-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600">
                    Open your authenticator app and enter the 6-digit code
                  </p>
                </div>
                
                <div>
                  <label htmlFor="mfaCode" className="block text-sm font-semibold text-gray-700 mb-2">
                    Authentication Code
                  </label>
                  <input
                    id="mfaCode"
                    name="mfaCode"
                    type="text"
                    maxLength={6}
                    autoComplete="off"
                    required
                    className="block w-full px-4 py-4 border-2 border-gray-200 rounded-xl text-center text-2xl font-mono tracking-widest text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 shadow-sm hover:shadow-md"
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>

                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setMfaStep('password');
                      setMfaCode('');
                      setMfaFactorId(null);
                      setError('');
                    }}
                    className="text-sm text-indigo-600 hover:text-indigo-500 transition-colors"
                  >
                    ← Back to password
                  </button>
                </div>
              </div>
            )}

            {/* Messages */}
            {info && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-800">{info}</p>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-lg font-semibold rounded-xl text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-100 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  mfaStep === 'password' ? 'Sign In' : 'Verify & Sign In'
                )}
              </button>
              {mfaStep === 'password' && (
                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={handleSendPasswordReset}
                    disabled={loading}
                    className="text-sm text-[#00a67e] transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <p className="text-sm text-gray-500">
              Secure admin access for Deal Once
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
